/**
 * Utility to process Lottie animation data for dark mode
 * Converts black colors to Techxeed primary blue (#07A8BC) while preserving green (#3FE0D0)
 */

// Techxeed colors
const PRIMARY_BLUE = '#07A8BC';
const SECONDARY_GREEN = '#3FE0D0';

// Black color variations to convert
const BLACK_COLORS = [
	'#000000',
	'#232323',
	'rgb(0,0,0)',
	'rgb(35,35,35)',
	'rgba(0,0,0,1)',
	'rgba(35,35,35,1)',
];

// Green color variations to preserve
const GREEN_COLORS = [
	'#3FE0D0',
	'#3fdfcf',
	'rgb(63, 223, 207)',
	'rgba(63, 223, 207, 1)',
];

/**
 * Check if a color string is black (or very dark)
 */
function isBlackColor(color: string): boolean {
	if (!color) return false;
	const normalized = color.toLowerCase().trim();
	
	// Check exact matches
	if (BLACK_COLORS.some(black => normalized === black.toLowerCase())) {
		return true;
	}
	
	// Check RGB/RGBA values
	const rgbMatch = normalized.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
	if (rgbMatch) {
		const r = parseInt(rgbMatch[1]);
		const g = parseInt(rgbMatch[2]);
		const b = parseInt(rgbMatch[3]);
		// Consider very dark colors (close to black) as black
		return r < 50 && g < 50 && b < 50;
	}
	
	// Check hex values
	const hexMatch = normalized.match(/^#([0-9a-f]{6})$/i);
	if (hexMatch) {
		const hex = hexMatch[1];
		const r = parseInt(hex.substring(0, 2), 16);
		const g = parseInt(hex.substring(2, 4), 16);
		const b = parseInt(hex.substring(4, 6), 16);
		return r < 50 && g < 50 && b < 50;
	}
	
	return false;
}

/**
 * Check if a color string is green (Techxeed secondary)
 */
function isGreenColor(color: string): boolean {
	if (!color) return false;
	const normalized = color.toLowerCase().trim();
	
	// Check exact matches
	if (GREEN_COLORS.some(green => normalized === green.toLowerCase())) {
		return true;
	}
	
	// Check RGB/RGBA values for green
	const rgbMatch = normalized.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
	if (rgbMatch) {
		const r = parseInt(rgbMatch[1]);
		const g = parseInt(rgbMatch[2]);
		const b = parseInt(rgbMatch[3]);
		// Check if it's close to Techxeed green (#3FE0D0 = rgb(63, 223, 207))
		return Math.abs(r - 63) < 20 && Math.abs(g - 223) < 20 && Math.abs(b - 207) < 20;
	}
	
	// Check hex values
	const hexMatch = normalized.match(/^#([0-9a-f]{6})$/i);
	if (hexMatch) {
		const hex = hexMatch[1];
		const r = parseInt(hex.substring(0, 2), 16);
		const g = parseInt(hex.substring(2, 4), 16);
		const b = parseInt(hex.substring(4, 6), 16);
		// Check if it's close to Techxeed green
		return Math.abs(r - 63) < 20 && Math.abs(g - 223) < 20 && Math.abs(b - 207) < 20;
	}
	
	return false;
}

/**
 * Recursively process Lottie animation data to convert black to blue while preserving green
 */
function processLottieColors(obj: any, path: string = ''): any {
	if (obj === null || obj === undefined) {
		return obj;
	}
	
	if (Array.isArray(obj)) {
		return obj.map((item, index) => processLottieColors(item, `${path}[${index}]`));
	}
	
	if (typeof obj === 'object') {
		const processed: any = {};
		
		for (const key in obj) {
			const currentPath = path ? `${path}.${key}` : key;
			const value = obj[key];
			
			// Handle color properties in Lottie
			// Colors can be in shapes, fills, strokes, etc.
			if (key === 'c' || key === 'fc' || key === 'sc' || key === 'k') {
				// Color properties: c (color), fc (fill color), sc (stroke color), k (keyframe value)
				if (Array.isArray(value)) {
					// Check if it's a color array [r, g, b] or [r, g, b, a] (values 0-1)
					if (value.length >= 3 && typeof value[0] === 'number' && value[0] <= 1) {
						const r = value[0] * 255;
						const g = value[1] * 255;
						const b = value[2] * 255;
						
						// Check if it's black (very dark)
						if (r < 50 && g < 50 && b < 50) {
							// Convert to blue: #07A8BC = rgb(7, 168, 188)
							processed[key] = [7 / 255, 168 / 255, 188 / 255, value[3] ?? 1];
						} else if (Math.abs(r - 63) < 20 && Math.abs(g - 223) < 20 && Math.abs(b - 207) < 20) {
							// Preserve green: #3FE0D0 = rgb(63, 223, 207)
							processed[key] = [63 / 255, 223 / 255, 207 / 255, value[3] ?? 1];
						} else {
							processed[key] = processLottieColors(value, currentPath);
						}
					} else {
						// Might be keyframes array - process each item
						processed[key] = processLottieColors(value, currentPath);
					}
				} else if (typeof value === 'string') {
					if (isBlackColor(value)) {
						processed[key] = PRIMARY_BLUE;
					} else if (isGreenColor(value)) {
						processed[key] = SECONDARY_GREEN;
					} else {
						processed[key] = value;
					}
				} else if (typeof value === 'object' && value !== null) {
					// Color might be in an object with k property (keyframes)
					processed[key] = processLottieColors(value, currentPath);
				} else {
					processed[key] = value;
				}
			} else if (key === 'shapes' || key === 'it' || key === 'layers' || key === 'assets') {
				// Recursively process shapes, items, layers, and assets
				processed[key] = processLottieColors(value, currentPath);
			} else {
				// Process other properties
				processed[key] = processLottieColors(value, currentPath);
			}
		}
		
		return processed;
	}
	
	return obj;
}

/**
 * Process Lottie animation data for dark mode
 * Converts black colors to Techxeed primary blue while preserving green
 */
export function processLottieForDarkMode(animationData: any): any {
	if (!animationData) {
		return animationData;
	}
	
	// Deep clone to avoid mutating original
	const cloned = JSON.parse(JSON.stringify(animationData));
	
	// Process the animation data
	return processLottieColors(cloned);
}

